import { Icon, Spinner, SpinnerSize, Tab } from "@blueprintjs/core";
import { useApiEffect } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";

import { CompetitionRequestApi, ERequestState, ERequestType, RequestApi } from "../../../../api";
import {
  BackButton,
  ConditionTab,
  LinkedSubFilesTab,
  PageBase,
  ProtectedTabs,
  RequestHistoryTab,
  RequestStickyMenu,
  VerificationTab,
} from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { MailLogListPage } from "../../../mail-log/MailLogListPage";
import { BillTab } from "../../BillTab";
import { CompetitionRequestDetail } from "./CompetitionRequestDetail";
import { JudgeValidationDetail } from "./JudgeValidationDetail";
import { ExpoTab } from "./ExpoTab";
import { CompetitionRequestBreedsPanel } from "./CompetitionRequestBreedsPanel";

export interface ICompetitionRequestItemPageProps {}

export const CompetitionRequestItemPage: React.FunctionComponent<ICompetitionRequestItemPageProps> = (props) => {
  const { id, tab, state } = useParams<{
    id: string;
    tab: string;
    state: string;
  }>();
  const api = useApiService(CompetitionRequestApi);
  const requestApi = useApiService(RequestApi);
  const history = useHistory();
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();

  //===================== Request info==================================
  const [requestInfo, requestInfoLoading, , refreshRequestInfo] = useApiEffect(
    () => requestApi.getRequestInfo(ERequestType.CompetitionRequest, +id, null, currentLanguage.languageId),
    [requestApi, id, currentLanguage]
  );

  const refreshRequest = React.useCallback(async () => {
    await refreshRequestInfo();
    if (requestInfo?.requestStateCode === ERequestState.REQUESTED) {
      await api.createCompetition(+id);
    }
  }, [api, id, refreshRequestInfo, requestInfo]);

  const closeRequest = React.useCallback(async () => {
    await api.close(requestInfo?.parentRequestId);
  }, [api, requestInfo]);

  //========================Return statement===========================

  return (
    <PageBase
      isLoading={requestInfoLoading}
      breadCrumbs={[
        {
          text: t(ETLCodes.CompetitionRequests),
          route: ERoutes.competitionRequest,
        },
        {
          text:
            +id > 0 ? (
              requestInfoLoading ? (
                <Spinner size={SpinnerSize.SMALL} />
              ) : (
                requestInfo.requestNumber
              )
            ) : (
              t(ETLCodes.New)
            ),
        },
      ]}
      actionHistory={
        +id > 0 &&
        !requestInfoLoading && {
          tlCode: ETLCodes.ActionHistoryRsh,
          name: requestInfo.requestNumber,
          id: +id,
        }
      }
      stickyContent={() => {
        if (requestInfo?.requestId > 0) {
          return (
            <RequestStickyMenu
              requestId={requestInfo.parentRequestId}
              requestInfo={requestInfo}
              refreshFunc={refreshRequest}
              closeFunc={closeRequest}
            />
          );
        } else {
          return null;
        }
      }}
    >
      <ProtectedTabs
        id="TabsCompetitionRequest"
        onChange={(newTabId) => history.push(`${ERoutes.competitionRequest}/${+id}/${newTabId}/${state}`)}
        selectedTabId={tab}
        renderActiveTabPanelOnly
      >
        <BackButton backRoute={ERoutes.competitionRequest} />
        <Tab
          id="detail"
          title={
            <div>
              {t(ETLCodes.Detail)} {requestInfo?.hasWarning && <Icon icon="warning-sign" intent="danger" />}
            </div>
          }
          panel={<CompetitionRequestDetail competitionRequestId={+id} editMode={!state || state === "edit"} />}
        />
        <Tab
          id="breeds"
          title={t(ETLCodes.Breeds)}
          disabled={+id <= 0}
          panel={<CompetitionRequestBreedsPanel competitionRequestId={+id} editMode={!state || state === "edit"} />}
        />
        <Tab
          id="verifications"
          title={t(ETLCodes.Verifications)}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <VerificationTab
                requestId={requestInfo.parentRequestId}
                editMode={!state || state === "edit"}
                statusETLCode={ETLCodes.VerificationsToObtainBilledStatus}
              />
            )
          }
          disabled={+id < 1}
        />
        {requestInfo &&
          (requestInfo?.requestStateCode === ERequestState.JUDGE_VALIDATION ||
            requestInfo?.requestStateCode === ERequestState.AUTHORIZED ||
            requestInfo?.requestStateCode === ERequestState.CATALOG_JUDGE_VALIDATION) && (
            <Tab
              id="judgeValidation"
              title={t(ETLCodes.JudgesValidation)}
              panel={<JudgeValidationDetail competitionRequestId={+id} editMode={!state || state === "edit"} />}
            />
          )}

        <Tab
          id="expos"
          title={t(ETLCodes.Expos)}
          disabled={+id <= 0}
          panel={requestInfo ? <ExpoTab competitionRequestId={+id} editMode={!state || state === "edit"} /> : null}
        />
        <Tab
          id="conditions"
          title={t(ETLCodes.Conditions)}
          disabled={+id <= 0}
          panel={
            requestInfo ? (
              <ConditionTab
                requestId={requestInfo.parentRequestId}
                requestState={requestInfo.requestStateCode}
                editMode={!state || state === "edit"}
                statusETLCode={ETLCodes.Conditions}
              />
            ) : null
          }
        />
        <Tab
          id="subrequests"
          title={t(ETLCodes.SubRequests, {
            count: requestInfoLoading ? `` : `(${requestInfo.subRequestCount})`,
          })}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <LinkedSubFilesTab requestInfo={requestInfo} editMode={state === "edit"} />
            )
          }
          disabled
        />
        <Tab
          id="billing"
          title={t(ETLCodes.Billing)}
          panel={
            <BillTab
              requestId={requestInfo?.parentRequestId}
              disabled={requestInfo?.requestStateCode !== ERequestState.CANCELLED}
            />
          }
          disabled={
            requestInfo?.requestStateCode === ERequestState.REQUESTED ||
            requestInfo?.requestStateCode === ERequestState.JUDGE_VALIDATION ||
            requestInfo?.requestStateCode === ERequestState.AUTHORIZED
          }
        />
        <Tab
          id="history"
          title={t(ETLCodes.History)}
          panel={requestInfoLoading ? <Spinner /> : <RequestHistoryTab requestId={requestInfo.parentRequestId} />}
          disabled={+id <= 0}
        />
        <Tab
          id="mailing"
          title={t(ETLCodes.Email)}
          panel={requestInfoLoading ? <Spinner /> : <MailLogListPage requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
      </ProtectedTabs>
    </PageBase>
  );
};
